import { fetchAuthSession, fetchUserAttributes, FetchUserAttributesOutput } from 'aws-amplify/auth';

/**
 * Extrae el tenantId del ID Token de Cognito (propiedad personalizada `custom:tenantId`)
 * @throws Error si el token no contiene el tenantId
 * @returns tenantId como string
 */
export async function getTenantId(): Promise<string> {
    const session = await fetchAuthSession();

    const tenantId = session.tokens?.idToken?.payload['custom:tenantId'];

    if (!tenantId) {
        throw new Error('No se pudo obtener el tenantId del token JWT del usuario');
    }

    return `${tenantId}`;
}

/**
 * Extrae el roleId del ID Token de Cognito (propiedad personalizada `custom:roleId`)
 * @throws Error si el token no contiene el roleId
 * @returns roleId como string
 */
export async function getRoleId(): Promise<string> {
    const session = await fetchAuthSession();

    const roleID = session.tokens?.idToken?.payload['custom:roleId'];

    // if (!roleID) {
    //     throw new Error('No se pudo obtener el roleId del token JWT del usuario');
    // }

    return `${roleID || ""}`;
}

/**
 * Extraer el store del ID Token de Cognito. Se acepta tanto el atributo personalizado
 * `custom:storeId` como el claim plano `storeId` que inyecta el trigger pre-token-generation.
 * @throws Error si el token no contiene el storeId
 * @returns storeId como string
 */
export async function getStoreId(): Promise<string> {
    const session = await fetchAuthSession();

    const payload = session.tokens?.idToken?.payload;
    const storeId = payload?.['custom:storeId'] ?? payload?.['storeId'];

    if (!storeId) {
        throw new Error('No se pudo obtener el storeId del token JWT del usuario');
    }

    return `${storeId || ""}`;
}

/**
 * Extrae la terminal del ID Token de Cognito. Se acepta tanto el atributo personalizado
 * `custom:terminalId` como el claim plano `terminalId` que inyecta el trigger
 * pre-token-generation. No lanza si falta: el llamador aplica la terminal por defecto.
 * @returns terminalId como string, o "" si el usuario no tiene terminal asignada
 */
export async function getTerminalId(): Promise<string> {
    const session = await fetchAuthSession();

    const payload = session.tokens?.idToken?.payload;
    const terminalId = payload?.['custom:terminalId'] ?? payload?.['terminalId'];

    return `${terminalId || ""}`;
}

/**
 * Extrae el userData de ID Token de Cognito 
 * @throws Error si el token no contiene el userData
 * @returns userData como FetchUserAttributesOutput
 */
export async function getUserData(): Promise<FetchUserAttributesOutput> {
    return await fetchUserAttributes();
}

export async function isAdmin(): Promise<boolean> {
  const session = await fetchAuthSession();
  const idToken = session.tokens?.idToken?.payload; // payload decodificado del JWT

  const groups = idToken["cognito:groups"] as string[] | undefined;
  return Array.isArray(groups) && groups.includes("admin");
}

/**
 * Etiqueta del usuario autenticado para dejar trazabilidad en los registros
 * (mismo tratamiento que `Productlog.user`): nombre si existe, si no el email.
 */
export async function resolveCurrentUserLabel(): Promise<string> {
    try {
        const { email = "", name = "" } = await getUserData();
        return name || email;
    } catch {
        return "";
    }
}

/**
 * Estampa en `user` la etiqueta del usuario autenticado como creador del registro.
 *
 * Se asigna siempre, pisando lo que traiga el input: al duplicar un documento el formulario
 * arrastra los valores del origen, y el creador tiene que ser quien guarda ahora.
 * Solo para creación; en `update` el valor ya persistido se conserva.
 */
export async function stampCreatorUser<T extends { user?: string | null }>(input: T): Promise<T> {
    return { ...input, user: await resolveCurrentUserLabel() };
}

/**
 * Renueva el ID token cuando se acaba de cambiar el nombre del propio usuario.
 *
 * Las lambdas de documentos (`save-edocument`, `cancel-edocument`, `import-edocuments`)
 * etiquetan con el claim plano `name` que inyecta el trigger pre-token-generation, y ese
 * claim solo se regenera al refrescar el token. Sin esto el usuario seguiria firmando con
 * el nombre viejo hasta que expire la sesion, mientras el frontend (que lee el atributo en
 * vivo con `fetchUserAttributes`) ya muestra el nuevo.
 *
 * Limitacion aceptada: si un admin cambia el nombre de *otro* usuario, el token de ese
 * usuario se renueva solo al expirar. Resolverlo exigiria un `AdminGetUser` por documento
 * emitido, que no compensa.
 */
export async function refreshSessionIfCurrentUser(email: string): Promise<void> {
    try {
        const { email: currentEmail = "" } = await getUserData();
        if (currentEmail.trim().toLowerCase() !== email.trim().toLowerCase()) return;
        await fetchAuthSession({ forceRefresh: true });
    } catch {
        // El refresco es best-effort: un fallo aqui no debe romper el guardado.
    }
}
